import templateReplace from 'es6-template-strings';
import _orderBy from 'lodash/orderBy';
import { useEffect, useState } from 'react';
import _find from 'lodash/find';

import { get, isEmpty } from 'ravenjs/utils/lodash';

import { PREPPED_ANSWERS } from 'constants/tickets';
import { getFormattedTimestamp } from 'utils/date';

export function usePreppedAnswersHistory({
    fetchUsers,
    preppedAnswerModificationsAttributes,
    preppedAnswerTransitionsAttributes,
    suggestedUpdatesAttributes,
    suggestUpdateTransitionsAttributes,
    vpaRequestAttributes,
    vpaRequestTransitionsAttributes,
}) {
    const [data, setData] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        function formatPreppedAnswerTransitionsAttributes(attributes) {
            let data = null;
            switch (attributes.event_name) {
                case 'nominated': {
                    data = {
                        createdAt: attributes.created_at,
                        requesterId: parseInt(attributes.requester_id, 10),
                        message: PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_FOR_VPA,
                        currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.PENDING,
                    };
                    break;
                }
                case 'downgraded': {
                    data = {
                        createdAt: attributes.created_at,
                        requesterId: attributes.requester_id,
                        message: PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_DOWNGRADE,
                        currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.DECLINED,
                    };
                    break;
                }
                default:
                    break;
            }

            return data;
        }

        function formatPreppedAnswerModificationsAttributes(attributes) {
            let data = null;

            switch (attributes.event) {
                case 'update': {
                    const { object_changes } = attributes;
                    if (object_changes?.expiration_date) {
                        const [
                            prevExpirationDate,
                            updatedExpirationDate,
                        ] = object_changes.expiration_date;

                        const formattedPrevExpirationDate = getFormattedTimestamp(
                            prevExpirationDate,
                            'MMM DD YYYY',
                            'UTC'
                        );
                        const formattedUpdatedExpirationDate = getFormattedTimestamp(
                            updatedExpirationDate,
                            'MMM DD YYYY',
                            'UTC'
                        );

                        data = {
                            createdAt: attributes.created_at,
                            requesterId: parseInt(attributes.user_id, 10),
                            message: templateReplace(
                                PREPPED_ANSWERS.HISTORY.MESSAGES.UPDATED_EXPIRATION_DATE,
                                {
                                    previousExpirationDate: formattedPrevExpirationDate,
                                    updatedExpirationDate: formattedUpdatedExpirationDate,
                                }
                            ),
                        };
                    }
                    if (!isEmpty(object_changes?.user_id)) {
                        data = {
                            createdAt: attributes.created_at,
                            requesterId: parseInt(attributes.user_id, 10),
                            userIds: object_changes.user_id,
                        };
                    }
                    break;
                }
                default:
                    break;
            }

            return data;
        }

        function formatSuggestedUpdatesAttributes(attributes) {
            let data = null;

            const transition = _find(suggestUpdateTransitionsAttributes, {
                prepped_answer_suggested_edit_id: attributes.suggestedUpdateId,
            });

            switch (attributes.current_state) {
                case 'approved': {
                    data = {
                        createdAt: transition.created_at,
                        requesterId: parseInt(transition.user_id, 10),
                        message: PREPPED_ANSWERS.HISTORY.MESSAGES.SUGGESTED_UPDATE_APPROVED,
                        currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.APPROVED,
                    };
                    break;
                }
                case 'declined': {
                    data = {
                        createdAt: transition.created_at,
                        requesterId: parseInt(transition.user_id, 10),
                        message: PREPPED_ANSWERS.HISTORY.MESSAGES.SUGGESTED_UPDATE_DECLINED,
                        currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.DECLINED,
                    };
                    break;
                }
                default:
                    break;
            }

            return data;
        }

        function formatVPARequestAttributes(attributes) {
            let data = null;
            switch (attributes.kind) {
                case 'nomination': {
                    if (attributes.status === 'completed') {
                        data = {
                            createdAt: attributes.status_as_of,
                            requesterId: attributes.requester_id,
                            message: PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_COMPLETE,
                            currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.ACCEPTED,
                        };
                    } else if (attributes.status === 'cancelled') {
                        data = {
                            createdAt: attributes.status_as_of,
                            requesterId: attributes.requester_id,
                            message: PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_DECLINE,
                            currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.DECLINED,
                        };
                    }
                    break;
                }
                case 'update': {
                    data = {
                        createdAt: attributes.created_at,
                        requesterId: attributes.requester_id,
                        message: PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_SUGGEST_UPDATE,
                        currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.PENDING,
                    };
                    break;
                }
                default:
                    break;
            }

            return data;
        }

        function formatVPARequestTransitionsAttributes(attributes) {
            let data = null;
            switch (attributes.kind) {
                case 'update': {
                    const filteredData = vpaRequestTransitionsAttributes.find(
                        item => item.vpa_request_id === attributes.vpaRequestId
                    );
                    if (filteredData?.to_state === 'completed') {
                        data = {
                            createdAt: filteredData.updated_at,
                            requesterId: attributes.requester_id,
                            message:
                                PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_SUGGEST_UPDATE_ACCEPT,
                            currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.ACCEPTED,
                        };
                    } else if (filteredData?.to_state === 'cancelled') {
                        data = {
                            createdAt: filteredData.updated_at,
                            requesterId: attributes.requester_id,
                            message:
                                PREPPED_ANSWERS.HISTORY.MESSAGES.NOMINATE_SUGGEST_UPDATE_DECLINE,
                            currentState: PREPPED_ANSWERS.HISTORY.CURRENT_STATE.DECLINED,
                        };
                    }

                    break;
                }
                default:
                    break;
            }

            return data;
        }

        async function fetchUsersData() {
            try {
                setLoading(true);

                const filteredPATransitionData = preppedAnswerTransitionsAttributes.map(
                    attributes => {
                        return formatPreppedAnswerTransitionsAttributes(attributes);
                    }
                );

                const filteredPAModificationsData = (preppedAnswerModificationsAttributes || [])
                    .filter(item => {
                        const { event, object_changes } = item;
                        return (
                            event === 'update' &&
                            (object_changes?.expiration_date?.length > 0 ||
                                object_changes?.user_id?.length > 0)
                        );
                    })
                    .map(attributes => formatPreppedAnswerModificationsAttributes(attributes));

                const filteredSuggestedUpdatesAttributes =
                    suggestedUpdatesAttributes?.map(attributes => {
                        return formatSuggestedUpdatesAttributes(attributes);
                    }) || [];

                const filteredVPARequestData = vpaRequestAttributes.map(attributes => {
                    return formatVPARequestAttributes(attributes);
                });

                const filteredVPARequestTransitionData = vpaRequestAttributes.map(attributes => {
                    return formatVPARequestTransitionsAttributes(attributes);
                });

                const finalData = _orderBy(
                    [
                        ...filteredPATransitionData,
                        ...filteredPAModificationsData,
                        ...filteredSuggestedUpdatesAttributes,
                        ...filteredVPARequestData,
                        ...filteredVPARequestTransitionData,
                    ].filter(item => item !== null),
                    ['createdAt'],
                    ['desc']
                );
                const userIdSet = new Set();
                finalData.forEach(({ requesterId, userIds }) => {
                    requesterId !== null && userIdSet.add(requesterId);
                    userIds?.forEach(userId => userIdSet.add(userId));
                });

                const response = await fetchUsers({ userId: [...userIdSet] });

                const users = get(response, 'users', []);
                const usersMap = {};

                users.forEach(user => (usersMap[user.userId] = user.fullName));
                const newData = finalData.map(({ userIds, requesterId, ...rest }) => {
                    const data = {
                        ...rest,
                        requesterName: usersMap[requesterId] || PREPPED_ANSWERS.HISTORY.SYSTEM,
                    };

                    if (userIds) {
                        const userNames = userIds.map(userId => usersMap[userId]);
                        const fromUser = userNames[0];
                        const toUser = userNames[1];
                        data.message = templateReplace(
                            PREPPED_ANSWERS.HISTORY.MESSAGES.CHANGED_AUTHOR,
                            {
                                fromUser,
                                toUser,
                            }
                        );
                    }

                    return data;
                });

                setData(newData);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        fetchUsersData();
    }, [
        fetchUsers,
        preppedAnswerTransitionsAttributes,
        suggestedUpdatesAttributes,
        suggestUpdateTransitionsAttributes,
        vpaRequestAttributes,
        vpaRequestTransitionsAttributes,
        preppedAnswerModificationsAttributes,
    ]);

    return { data, error, loading };
}
